/**
 * 録音全体のうち「声が入っていた時間」の割合を測る（課題 #98 段階 1）。
 *
 * 黙ったまま 20 秒が過ぎても送信できてしまうため、送信前に
 * 「ほとんど声が入っていないようです」と気づかせる。差し戻しはしない。
 * 声の小さい方が「ちゃんと話したのに止められる」方が、無音の録音が 1 件
 * 混ざるより重い失敗なので、判定は「案内を出さない」側へ倒している。
 *
 * - 音量の閾値は無音検知（silenceDetector）と同じ低めの値を使う。少しでも
 *   声が出ていれば発話に数える
 * - 画面が裏に回ると描画が止まり、計測も止まる。戻ったときの 1 回で
 *   空いた時間をまとめて数えないよう、1 回に足す時間に上限を置く
 * - 計測できた時間が短すぎるときは判定しない（判定できないときは通す）
 *
 * 時間は呼び出し側から渡す。テストで実時間を待たずに検証できるようにするため。
 */
import { SILENCE_RMS_THRESHOLD } from './silenceDetector';

/**
 * この割合を下回ったら案内を出す。暫定値（2026-09-24 CENTRIC 了承）。
 * CENTRIC が demo で「黙ったまま 20 秒」と「普通に話した 20 秒」の比率を測り、
 * その結果で決め直す（課題 #98）。
 */
export const VOICED_RATIO_WARN_BELOW = 0.2;

/** 判定に必要な計測時間(ms)。これより短ければ判定しない */
export const MIN_MEASURED_MS = 10_000;

/** 1 回の更新で数える時間の上限(ms)。画面が裏に回った間の空白を数えない */
export const MAX_TICK_MS = 250;

export interface VoiceActivityStats {
  /** 声が入っていたと判定した時間(ms) */
  voicedMs: number;
  /** 計測できた時間(ms) */
  measuredMs: number;
  /** voicedMs / measuredMs。判定に足りないときは null */
  ratio: number | null;
}

export interface VoiceActivityMeter {
  /** 音量サンプルを 1 つ与えて計測を進める */
  update(rms: number, nowMs: number): void;
  /** この時刻までの時間を数えずに進める（課題 #160。お題の音声を流して録音を止めている間） */
  skip(nowMs: number): void;
  readonly stats: VoiceActivityStats;
}

export function createVoiceActivityMeter(
  threshold: number = SILENCE_RMS_THRESHOLD
): VoiceActivityMeter {
  let voicedMs = 0;
  let measuredMs = 0;
  let lastAt: number | null = null;

  return {
    update(rms: number, nowMs: number) {
      // 初回は差分が取れないので基準時刻を置くだけ
      if (lastAt === null) {
        lastAt = nowMs;
        return;
      }
      // 時計が巻き戻っても負の時間を足さない。空白が長くても上限までしか数えない
      const dt = Math.min(MAX_TICK_MS, Math.max(0, nowMs - lastAt));
      lastAt = nowMs;
      measuredMs += dt;
      if (rms > threshold) voicedMs += dt;
    },
    skip(nowMs: number) {
      lastAt = nowMs;
    },
    get stats(): VoiceActivityStats {
      return {
        voicedMs,
        measuredMs,
        ratio: measuredMs >= MIN_MEASURED_MS ? voicedMs / measuredMs : null,
      };
    },
  };
}

/** 案内を出すか。計測が足りない（ratio が null）ときは出さない */
export function isMostlySilent(stats: VoiceActivityStats | null): boolean {
  return stats?.ratio != null && stats.ratio < VOICED_RATIO_WARN_BELOW;
}

/** 発話割合の確認表示を覚えておくキー（同じタブの間だけ） */
export const VOICE_CHECK_STORAGE_KEY = 'ceeu.voiceCheck';

/**
 * 発話割合を数値で出す確認表示を使うか（課題 #98）。
 * CENTRIC が demo で基準値を決めるための表示で、利用者には出さない。
 * `?voice_check=1` で有効、`?voice_check=0` で解除。録り直しや画面遷移で
 * クエリが落ちても続けて測れるよう、同じタブの間は覚えておく。
 * 保存領域が使えない環境でも、クエリの指定だけは効かせる。
 */
export function resolveVoiceCheckFlag(
  search: string,
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null
): boolean {
  const param = new URLSearchParams(search).get('voice_check');
  try {
    if (param === '1') {
      storage?.setItem(VOICE_CHECK_STORAGE_KEY, '1');
      return true;
    }
    if (param === '0') {
      storage?.removeItem(VOICE_CHECK_STORAGE_KEY);
      return false;
    }
    return storage?.getItem(VOICE_CHECK_STORAGE_KEY) === '1';
  } catch {
    return param === '1';
  }
}
